import { declareTheme } from "./themes";

export default declareTheme({
  VTab: {
    normal: {
      wrapper: 'px-2',
      content: 'pt-3 pb-2.5 text-sm leading-5',
    },
    small: {
      wrapper: 'px-2',
      content: 'pt-1.5 pb-1 text-sm leading-5',
    },
    tiny: {
      wrapper: 'px-1',
      content: 'pt-1.5 pb-1 text-xs leading-4',
    },
    active: 'text-primary-500',
    underline: true
  },
  VTabPanel: {
    normal: {
      iconSize: 'sm'
    },
    small: {
      iconSize: 'sm'
    },
    tiny: {
      iconSize: 'xs'
    },
    wrapper: 'px-6',
    buttonWrapper: ''
  },
  VAvatar: {
    container: 'bg-primary-500 text-neutral-100'
  },
  VTextArea: {
    content: {
      disabled: 'text-neutral-400 bg-neutral-150 ring-neutral-200',
      readonly: 'text-neutral-950 bg-neutral-150 ring-0',
      enabled: 'text-neutral-950 bg-neutral-100 ring-neutral-300'
    }
  },
  VInput: {
    content: {
      disabled: 'text-neutral-400',
      readonly: 'text-neutral-950',
      enabled: 'text-neutral-950',
    },
    wrapper: {
      default: {
        disabled: 'text-neutral-400 bg-neutral-150 shadow-neutral-200',
        readonly: 'text-neutral-950 bg-neutral-150 shadow-none',
        enabled: 'text-neutral-950 bg-neutral-100 shadow-neutral-300',
      },
      underlined: {
        disabled: 'text-neutral-400 shadow-neutral-200',
        readonly: 'text-neutral-950 shadow-neutral-200',
        enabled: 'text-neutral-950 shadow-neutral-300',
      }
    }
  },
  VNumber: {
    container: 'gap-2',
    content: {
      base: 'rounded-md',
      enabled: 'text-neutral-950 bg-neutral-100',
      disabled: 'text-neutral-400 bg-neutral-150',
      readonly: 'text-neutral-950 bg-neutral-150'
    },
    wrapper: {
      enabled: 'ring-neutral-300',
      disabled: 'ring-neutral-200',
      readonly: 'ring-0'
    },
    focus: true
  },
  VSLabel: {
    text: 'text-sm text-neutral-950 font-medium'
  },
  VSDescription: {
    text: 'text-sm text-secondary'
  },
  VContextMenu: {
    container: 'rounded-lg shadow-sm ring-1 ring-inset ring-neutral-250'
  },
  VSSelectBase: {
    outline: true,
    focus: true,
    button: {
      base: 'px-3 py-2 text-sm leading-5',
      text: 'text-neutral-850'
    },
    icon: {
      size: 'sm',
      up: 'Solid/chevron-up-v2',
      down: 'Solid/chevron-down-v2',
      color: 'neutral/450'
    },
    option: {
      text: 'text-neutral-850'
    }
  },
  VSContextMenuItem: {
    container: 'min-h-9 h-9 text-sm gap-3',
    label: 'pl-3 pr-1',
    menu: 'pr-2',
    icon: 'base',
    button: {
      base: 'px-3',
      colors: {
        default: {
          enabled: 'hover:bg-neutral-150 text-neutral-950',
          disabled: 'text-neutral-400'
        },
        red: {
          enabled: 'hover:bg-red-100 text-red-500',
          disabled: 'text-red-400'
        }
      }
    }
  },
  VSwitch: {
    xs: {
      container: 'min-h-4',
      button: {
        base: 'h-4 w-7 p-0.5 before:w-3 before:h-3 before:left-0.5 before:top-0.5 bg-neutral-250 hover:bg-neutral-200 active:bg-neutral-300',
        active: 'before:translate-x-3 !bg-primary-500 hover:!bg-primary-450 active:!bg-primary-550'
      }
    },
    sm: {
      container: 'min-h-5',
      button: {
        base: 'h-5 w-9 p-0.5 before:w-4 before:h-4 before:left-0.5 before:top-0.5 bg-neutral-250 hover:bg-neutral-200 active:bg-neutral-300',
        active: 'before:translate-x-4 !bg-primary-500 hover:!bg-primary-450 active:!bg-primary-550'
      }
    },
    lg: {
      container: 'min-h-7',
      button: {
        base: 'h-7 w-14 p-0.5 before:w-6 before:h-6 before:left-0.5 before:top-0.5 bg-neutral-250 hover:bg-neutral-200 active:bg-neutral-300',
        active: 'before:translate-x-7 !bg-primary-500 hover:!bg-primary-450 active:!bg-primary-550'
      }
    },
    xl: {
      container: 'min-h-8',
      button: {
        base: 'h-8 w-16 p-0.5 before:w-7 before:h-7 before:left-0.5 before:top-0.5 bg-neutral-250 hover:bg-neutral-200 active:bg-neutral-300',
        active: 'before:translate-x-8 !bg-primary-500 hover:!bg-primary-450 active:!bg-primary-550'
      }
    },
    base: {
      container: 'min-h-6',
      button: {
        base: 'h-6 w-11 min-w-11 p-0.5 before:w-5 before:h-5 before:left-0.5 before:top-0.5 bg-neutral-250 hover:bg-neutral-200 active:bg-neutral-300',
        active: 'before:translate-x-5 !bg-primary-500 hover:!bg-primary-450 active:!bg-primary-550'
      }
    }
  },
  VButton: {
    button: 'rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500',
    colors: {
      'contained/primary': 'border-primary-500 bg-primary-500 text-neutral-100 hover:bg-primary-450 hover:border-primary-450 active:bg-primary-550 active:border-primary-550',
      'outlined/primary': 'border-primary-500 bg-primary-100 text-primary-500 hover:text-primary-450 hover:border-primary-450 active:bg-primary-150 active:text-primary-550 active:border-primary-550',
      'text/primary': 'text-primary-500 hover:text-primary-450 active:text-primary-550',
      'contained/secondary': '',
      'outlined/secondary': 'border-neutral-300 hover:border-neutral-250 active:border-neutral-350 bg-neutral-100 active:bg-neutral-150 text-neutral-900 hover:text-neutral-850 active:text-neutral-950',
      'text/secondary': 'text-neutral-900 hover:text-neutral-850 active:text-neutral-950',
      'contained/tertiary': 'bg-neutral-50 border-neutral-500 text-neutral-950 hover:text-neutral-750 active:text-neutral-600',
      'outlined/tertiary': '',
      'text/tertiary': 'text-neutral-500 hover:text-neutral-450 active:text-neutral-600',
      'contained/red': 'border-red-500 bg-red-500 text-neutral-100 hover:bg-red-450 hover:border-red-450',
      'outlined/red': 'border-red-500 hover:border-red-450 active:border-red-550 bg-neutral-100 hover:bg-red-100 active:bg-red-150 text-red-500 hover:text-red-450 active:text-red-550 focus-visible:outline-red-500',
      'text/red': ''
    },
    sizes: {
      'xl': 'py-2.5 px-3.5 text-base leading-6 font-semibold',
      'lg': 'py-2 px-3 text-sm leading-5 font-semibold',
      'base': 'py-1.5 px-2.5 text-sm leading-5 font-semibold',
      'sm': 'py-1 px-2 text-sm leading-5 font-semibold',
      'xs': 'py-1 px-2 text-xs leading-4 font-semibold',
      'text_xl': 'text-base leading-6 font-semibold',
      'text_lg': 'text-sm leading-5 font-semibold',
      'text_base': 'text-sm leading-5 font-semibold',
      'text_sm': 'text-sm leading-5 font-semibold',
      'text_xs': 'text-xs leading-4 font-semibold',
      'icon_xl': 'p-2.5',
      'icon_lg': 'p-2',
      'icon_base': 'p-1.5',
      'icon_sm': 'p-1',
      'icon_xs': 'p-1'
    }
  }
})
